import "../App.css";

import {
  desktopBlockStyle,
  questionsStyle,
  mobileQuestionStyle,
} from "../styles";

import {
  questions,
  answers,
  introduction,
  comments,
  congratulations,
} from "../texts/quiz-qa";

import { useState } from "react";

import Popup from "./Popup";

import sendQuizCompleted from "../lib/sendQuizCompletedRequest";

interface QuizProps extends Omit<PageProps, "setContent" | "setModalOpen"> {
  isQuizCompleted: boolean;
  setQuizCompleted: SetBoolean;
}

export default function Quiz({
  isMobile,
  isVertical,
  isDark,
  isQuizCompleted,
  setQuizCompleted,
}: QuizProps) {
  const [answered, setAnswered] = useState(isQuizCompleted ? 8 : -1);
  const [questNumber, setQuestNumber] = useState("0");
  const [usedQuestions, setUsedQuestions] = useState<string[]>([]);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [guess, setGuess] = useState("");
  const [finalAnswer, setFinalAnswer] = useState("");

  const desktopStyle = `${desktopBlockStyle} flex flex-col h-full items-center ${
    isVertical
      ? "sm:p-8 sm:gap-8 lg:pt-20 lg:p-16 lg:gap-10"
      : "sm:p-8 sm:gap-8 2xl:p-20 2xl:gap-20"
  }`;
  const mobileStyle = `flex flex-col items-center ${
    isVertical ? (window.innerWidth < 321 ? "pt-4" : "pt-8") : "pt-4"
  } gap-8`;

  const isSmallScreen =
    window.visualViewport && window.visualViewport.height < 670;

  const addQuestion = () => {
    setGuess("");
    setAnswered(answered + 1);

    while (true) {
      const randomNumber = (Math.floor(Math.random() * 24) + 1).toString();
      if (!usedQuestions.includes(randomNumber)) {
        const updatedQuestionList = [...usedQuestions, randomNumber];
        setUsedQuestions(updatedQuestionList);
        setQuestNumber(randomNumber);
        break;
      }
    }
  };

  const checkSpecialQuestion = async () => {
    if (finalAnswer !== "while True") {
      setFinalAnswer("");
      setGuess("Wrong");
      setTimeout(() => setGuess(""), 500);
    } else {
      await sendQuizCompleted(setQuizCompleted);
      setGuess("Correct");
    }
  };

  return (
    <div className={isMobile ? mobileStyle : desktopStyle}>
      <p
        className={`${
          (isSmallScreen && !isVertical) || window.innerWidth < 321
            ? "hidden"
            : ""
        } text-${isMobile ? "2" : "3"}xl pl-2`}
      >
        Quiz time!
      </p>
      <div
        className={`flex flex-col justify-between items-center rounded-xl 
	  ${isDark ? "bg-gray-900" : "bg-red-100"}  
    ${
      isMobile
        ? `w-[90vw] p-4 ${
            window.innerWidth < 321 ? "h-[85vh]" : "min-h-[65vh]"
          } ${!isVertical ? "h-[90vh]" : ""}`
        : isVertical
        ? "w-full sm:h-[65vh] lg:h-[45vh] xl:h-[20vh] xl:w-[40vw] px-8 text-center gap-6"
        : `sm:w-11/12 2xl:w-[70vw] 3xl:w-[30vw] h-3/4 3xl:h-[30vh]`
    }
	  ${guess === "Wrong" ? "shake" : ""}`}
      >
        {answered === -1 && (
          <div
            className={`flex flex-col items-center ${
              isMobile
                ? isSmallScreen
                  ? !isVertical
                    ? "text-xs"
                    : "text-sm"
                  : ""
                : isVertical
                ? "px-6 pt-12 gap-20"
                : "sm:pt-8 sm:text-md sm:px-8 xl:pt-20 2xl:pt-32 gap-20"
            } `}
          >
            <div
              className={
                isMobile
                  ? "pb-8"
                  : "text-center sm:text-md xl:text-lg 2xl:text-xl"
              }
            >
              {introduction.map((sentence) => (
                <p className="pt-2" key={sentence}>
                  {sentence}
                  {!(isSmallScreen && !isVertical) && <br />}
                </p>
              ))}
            </div>
            <button
              className={`w-20 h-8 rounded-xl 
			  hover:bg-wine hover:text-white 
        ${isDark ? "bg-gray-700" : "bg-red-300"}`}
              onClick={addQuestion}
            >
              Начать
            </button>
          </div>
        )}
        {answered > -1 && answered < 7 && (
          <div
            className={
              isMobile
                ? `${mobileQuestionStyle} ${
                    window.innerWidth < 321 ? "pt-1 gap-4" : ""
                  }`
                : questionsStyle
            }
          >
            <p
              className={
                isMobile
                  ? `${
                      isSmallScreen ? (!isVertical ? "text-sm" : "") : "text-lg"
                    } text-center`
                  : "text-xl sm:text-center"
              }
            >
              {questions[questNumber].text}
            </p>
            <div
              className={
                isMobile
                  ? `flex ${
                      isVertical ? "flex-col" : "flex-row"
                    } gap-4 items-center`
                  : isVertical
                  ? "flex flex-col gap-4 items-center px-4"
                  : "flex sm:gap-4 xl:gap-10"
              }
            >
              {questions[questNumber].options.map((option) => (
                <button
                  key={option}
                  className={`
                  ${
                    isMobile
                      ? `${
                          isSmallScreen
                            ? !isVertical
                              ? "text-xs"
                              : "text-sm"
                            : ""
                        } py-1 w-full`
                      : "sm:py-2 xl:py-0 sm:text-xs xl:h-8 xl:w-max xl:text-lg"
                  } px-2  rounded-xl ${
                    guess === "Correct"
                      ? `${
                          option === answers[questNumber]
                            ? "bg-green-300 text-black"
                            : isDark
                            ? "bg-gray-700"
                            : "bg-white"
                        }`
                      : `${
                          isDark ? "bg-gray-700" : "bg-white"
                        } hover:bg-wine hover:text-white`
                  }`}
                  onClick={() => {
                    if (answers[questNumber] === option) {
                      setGuess("Correct");
                    } else {
                      setGuess("Wrong");
                      setTimeout(() => setGuess(""), 500);
                    }
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        )}
        {answered === 7 && (
          <div
            className={
              isMobile
                ? `${mobileQuestionStyle} ${
                    !isVertical || window.innerWidth < 321 ? "pt-1" : ""
                  }`
                : questionsStyle
            }
          >
            <p
              className={
                isMobile
                  ? !isVertical
                    ? window.innerWidth < 321
                      ? "text-lg"
                      : ""
                    : "text-lg text-center"
                  : "text-xl"
              }
            >
              Как расшифровываются единицы и нули на монете?
            </p>
            <div
              className={
                isMobile
                  ? `flex items-center ${
                      isVertical ? "flex-col gap-6" : "gap-4"
                    }`
                  : "flex gap-8"
              }
            >
              <div className="flex gap-4">
                <button
                  className={`rounded-full 
                  ${isDark ? "bg-gray-700" : "bg-white"} 
                  ${isMobile && !isVertical ? "px-2" : "h-8 px-3"}`}
                  onClick={() => setPopupOpen(!isPopupOpen)}
                >
                  ?
                </button>
                <input
                  onChange={(event) => setFinalAnswer(event.target.value)}
                  value={finalAnswer}
                  className={`rounded-lg text-center outline-none focus:outline-none 
                  ${isDark ? "bg-gray-700" : "bg-white"} 
                  ${isMobile && !isVertical ? "text-sm" : ""}`}
                  placeholder="Введи текст"
                />
              </div>
              <button
                className={`rounded-xl hover:bg-wine hover:text-white 
                ${isDark ? "bg-gray-700" : "bg-white"}
                ${isMobile && !isVertical ? "text-sm py-1 px-2" : "w-28 h-8 "}`}
                onClick={checkSpecialQuestion}
              >
                Отправить
              </button>
            </div>
            <Popup
              isDark={isDark}
              isOpen={isPopupOpen}
              setOpen={setPopupOpen}
              content={
                <p className="text-xs">
                  1110111 1101000 1101001 1101100 1100101 100000 1010100 1110010
                  1110101 1100101
                </p>
              }
            />
          </div>
        )}
        {guess === "Correct" && (
          <div
            className={`flex flex-col items-center ${
              isMobile ? "pt-4 gap-4" : "gap-20"
            }`}
          >
            <div
              className={`flex ${
                isDark ? "bg-gray-700" : "bg-light-header"
              } rounded-xl  
            ${
              isMobile
                ? `p-2 w-full ${
                    isSmallScreen ? "text-xs" : "text-sm"
                  } text-center`
                : "p-4 max-w-4xl"
            }`}
            >
              {answered < 7 ? comments[questNumber] : comments.special}
            </div>
            <button
              className={`rounded-xl hover:bg-wine hover:text-white 
              ${isDark ? "bg-gray-700" : "bg-white"}
              ${isMobile && !isVertical ? "text-sm px-2 py-1" : "w-32 h-8"}`}
              onClick={addQuestion}
            >
              Продолжить
            </button>
          </div>
        )}
        {answered === 8 && (
          <div className="flex flex-col items-center justify-center h-full gap-6">
            <div className={`${isMobile ? "text-lg" : "text-2xl"} text-center`}>
              {congratulations.map((sentence) => (
                <p className="pt-4" key={sentence}>
                  {sentence}
                </p>
              ))}
              <br />
            </div>
            <button
              className={`w-44 h-8 rounded-xl hover:bg-wine hover:text-white 
            ${isDark ? "bg-gray-700" : "bg-white"}`}
              onClick={() => {
                window.open("/photos", "_blank");
              }}
            >
              Открыть альбом
            </button>
          </div>
        )}
        {answered > -1 && answered < 8 && (
          <div
            className={`flex pb-10 text-lg ${
              isMobile ? (!isVertical ? "text-sm pt-6" : "pt-4") : ""
            }`}
          >
            <>Выполнено: </>
            {answered}
            <>/8 вопросов</>
          </div>
        )}
      </div>
      <div
        className={
          !isMobile && isVertical ? "sm:hidden lg:flex pt-12" : "hidden"
        }
        style={{ opacity: 0.7 }}
      >
        <img
          src={`/placeholder-${isDark ? "dark" : "light"}.png`}
          alt="placeholder"
        />
      </div>
    </div>
  );
}
